import type { ColumnData, ColumnProp, DataType } from '@revolist/react-datagrid';

import {
  SelectOption,
  SelectOptionsSource,
  getEditorOptions,
  resolveSelectOptions,
  toSelectOption,
} from './SelectCellEditor';
import { RevoGridBeforeRangeEditEvent } from './types';

export type RevoGridOptionColumns = Record<ColumnProp, SelectOptionsSource>;

export const REVO_GRID_RANGE_FILL_PROPS = {
  range: true,
  useClipboard: { rangeFill: true },
} as const;

const isClearedValue = (value: unknown) => value === '' || value === null || value === undefined;

const isAllowedValue = (options: readonly SelectOption[], value: unknown): boolean =>
  isClearedValue(value) || options.some((option) => toSelectOption(option).value === value);

export const getOptionColumns = (columns: ColumnData): RevoGridOptionColumns =>
  columns.reduce<RevoGridOptionColumns>((optionColumns, column) => {
    if ('children' in column) {
      return { ...optionColumns, ...getOptionColumns(column.children) };
    }

    const options = getEditorOptions(column.editor);

    return options ? { ...optionColumns, [column.prop]: options } : optionColumns;
  }, {});

/**
 * Drops the props this row is not allowed to write. Returns how many.
 *
 * `row` is only the props THIS edit is writing — a same-column copy that
 * doesn't touch Tag Type never carries one. A dependent column's option list
 * (Raw Unit/Data Type, keyed on Tag Type) is resolved against `currentRow`
 * (RevoGrid's own pre-edit snapshot for this row, from `event.detail.models`)
 * merged under `row`, so it sees the row's real Tag Type instead of none.
 */
const sanitizeRow = (
  row: DataType,
  optionColumns: RevoGridOptionColumns,
  currentRow: DataType | undefined
): number =>
  Object.keys(optionColumns).reduce((dropped, prop) => {
    if (!(prop in row)) return dropped;
    const options = resolveSelectOptions(optionColumns[prop], { ...currentRow, ...row });
    if (isAllowedValue(options, row[prop])) return dropped;
    // eslint-disable-next-line no-param-reassign
    delete row[prop];

    return dropped + 1;
  }, 0);

export const sanitizeRangeEditValues = (
  event: RevoGridBeforeRangeEditEvent,
  optionColumns: RevoGridOptionColumns
): number => {
  const data = event.detail?.data ?? {};
  const models = event.detail?.models ?? {};

  return Object.keys(data).reduce(
    (dropped, rowIndex) => dropped + sanitizeRow(data[+rowIndex], optionColumns, models[+rowIndex]),
    0
  );
};
